import React, { useEffect, useState } from "react";
import { Typography, Pagination, Button } from "antd";
import colors from "../../../utils/theme/colors";
import { motion } from "framer-motion";
import WorkflowGalleryItem from "./WorkflowGalleryItem";
import JobModal from "../../../components/jobModal/JobModal";


function WorkflowJobsList({ workflowJobs, pagination, onPageChange, onLoadMore }) {
  const [selectedJob, setSelectedJob] = useState({ visible: false, data: null });
  // Track viewport width for responsive grid columns
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleJobClick = (job) => setSelectedJob({ visible: true, data: job });



  // Empty state
  if (!workflowJobs || workflowJobs.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          textAlign: 'center',
          padding: 32,
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.6 }}>🎨</div>
        <Typography.Title level={4} style={{ color: colors.foreground, marginBottom: 8 }}>
          No generations yet
        </Typography.Title>
        <Typography.Text style={{ color: colors.muted_foreground, fontSize: 14 }}>
          Run your first workflow to see your creations here →
        </Typography.Text>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header matching ImageJobsList */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div
            style={{
              fontSize: 24,
              background: 'linear-gradient(135deg, #1677ff, #722ed1)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            🎨
          </div>
          <Typography.Text
            style={{
              color: colors.foreground,
              fontSize: 20,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #ffffff, #e6e6e6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Your Creations
          </Typography.Text>
        </div>
        <Typography.Text style={{ color: colors.muted_foreground, fontSize: 14, display: 'block' }}>
          {workflowJobs.length === 0
            ? 'Start creating to see your generations here'
            : `${workflowJobs.length} generation${workflowJobs.length !== 1 ? 's' : ''} • Latest first`}
        </Typography.Text>
      </div>

      {/* Gallery Grid */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isNarrow ? '1fr' : 'repeat(2, 1fr)',
            gap: 20,
            padding: '0 8px',
          }}
        >
          {workflowJobs
            .filter((job) => {
              if (!job) return false;
              const status = job.status;
              if (status === 'processing' || status === 'pending' || status === 'failed') return true;
              if (status === 'completed') {
                const arr = Array.isArray(job?.results?.results) ? job.results.results : [];
                return arr.some(
                  (r) =>
                    (r?.external_url || r?.content) &&
                    (r?.content_type === 'image' || r?.content_type === 'video' || r?.content_type === 'text' || !r?.content_type)
                );
              }
              return false;
            })
            .map((job) => (
              <motion.div
                key={job.workflow_job_id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                <WorkflowGalleryItem job={job} onJobClick={handleJobClick} />
              </motion.div>
            ))}
        </div>
      </div>

      {/* Load More / Pagination */}
      {(() => {
        // Alternative condition: check if we have loaded all items
        const hasMoreItems = (workflowJobs?.length || 0) < (pagination?.totalItems || 0);
        const shouldShow = pagination && hasMoreItems && onLoadMore;
        console.log('Load More Debug:', {
          pagination,
          shouldShow,
          hasMoreItems,
          currentPage: pagination?.page,
          totalPages: pagination?.totalPages,
          workflowJobsLength: workflowJobs?.length,
          totalItems: pagination?.totalItems
        });
        return shouldShow;
      })() && (
        <div style={{ padding: '16px 0', textAlign: 'center' }}>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              type="dashed"
              size="large"
              onClick={() => onLoadMore(parseInt(pagination.page, 10) + 1)}
              style={{
                background: 'rgba(255,255,255,0.05)',
                borderColor: 'rgba(255,255,255,0.2)',
                color: colors.foreground,
                borderRadius: 8,
                padding: '8px 24px',
                height: 'auto',
                fontWeight: 500,
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.1)';
                e.target.style.borderColor = 'rgba(255,255,255,0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.05)';
                e.target.style.borderColor = 'rgba(255,255,255,0.2)';
              }}
            >
              Load More ({Math.max(0, (pagination.totalItems || 0) - (workflowJobs?.length || 0))} remaining)
            </Button>
          </motion.div>
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, display: 'block', marginTop: 8, color: colors.muted_foreground }}
          >
            Showing {workflowJobs?.length || 0} of {pagination.totalItems || 0} runs
          </Typography.Text>
        </div>
      )}

      {/* Traditional pagination fallback */}
      {pagination && pagination.totalPages > 1 && !onLoadMore && (
        <div style={{ padding: '12px 0', background: colors.black, textAlign: 'center', borderRadius: 8 }}>
          <Pagination
            current={pagination?.page || 1}
            pageSize={pagination?.limit || 10}
            total={pagination?.totalItems || 0}
            onChange={onPageChange}
            showSizeChanger={false}
            size="small"
            style={{ color: colors.foreground }}
          />
        </div>
      )}

      {/* Job Modal */}
 
      <JobModal
        visible={selectedJob.visible}
        data={selectedJob.data}
        onClose={() => setSelectedJob({ visible: false, data: null })}
      />
    </div>
  );
}

export default WorkflowJobsList;
